import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Sparkles, Send, BookOpen, Plus, X, Clock } from "lucide-react";
import { toast } from "sonner";

export default function Advice() {
  const [articles, setArticles] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([
    { id: "welcome", role: "assistant", text: "Ti ascolto. Cosa cerchi oggi? Una via, una pratica, una compagnia per camminare?" }
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const endRef = useRef(null);
  const { user } = useAuth();

  const loadArticles = () => {
    axios.get(`${API}/articles`).then(r => {
      setArticles(r.data);
      if (r.data.length && !active) setActive(r.data[0]);
    });
  };

  useEffect(() => { loadArticles(); }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || busy) return;
    const userMsg = input.trim();
    setMessages(m => [...m, { id: `u-${Date.now()}`, role: "user", text: userMsg }]);
    setInput("");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/chat`, { message: userMsg, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMessages(m => [...m, { id: `a-${Date.now()}`, role: "assistant", text: r.data.reply }]);
    } catch {
      setMessages(m => [...m, { id: `err-${Date.now()}`, role: "assistant", text: "C'è stato un piccolo silenzio nella linea. Riprova tra un momento." }]);
    } finally { setBusy(false); }
  };

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-12 py-16" data-testid="advice-page">
      <div className="max-w-3xl mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-tc-secondary mb-3">Saperi che si tramandano</div>
          <h1 className="font-serif text-5xl md:text-6xl text-tc-primary">Consigli & tecniche</h1>
          <p className="text-tc-muted mt-4 text-lg">
            Articoli scritti dalla rete, e <em>Radice</em> — una guida AI gentile, sempre disponibile per ascoltare e suggerire.
          </p>
        </div>
        {user && (
          <button onClick={() => setShowForm(true)} className="btn-primary inline-flex items-center gap-2 self-start" data-testid="new-article-btn">
            <Plus className="w-4 h-4" /> Scrivi un articolo
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-12 gap-8">
        {/* Articles sidebar */}
        <aside className="md:col-span-4 space-y-2" data-testid="articles-list">
          <div className="text-xs uppercase tracking-widest text-tc-accent flex items-center gap-2 mb-3"><BookOpen className="w-3 h-3" /> Articoli</div>
          {articles.map(a => (
            <button key={a.id} onClick={() => setActive(a)}
              className={`text-left w-full p-4 rounded-2xl border transition-all ${active?.id === a.id ? 'bg-tc-surface border-tc-primary' : 'bg-transparent border-tc/40 hover:border-tc-primary/60'}`}
              data-testid={`article-${a.id}`}>
              <div className="text-[10px] uppercase tracking-widest text-tc-secondary">{a.category} · {a.read_time}</div>
              <div className="font-serif text-lg text-tc-primary mt-1 leading-tight">{a.title}</div>
            </button>
          ))}
        </aside>

        {/* Article content + chat */}
        <div className="md:col-span-8 space-y-10">
          {active && (
            <article className="bg-tc-surface rounded-3xl p-8 md:p-10 border border-tc/50" data-testid="article-content">
              <div className="text-xs uppercase tracking-widest text-tc-secondary mb-3">{active.category} — di {active.author}</div>
              <h2 className="font-serif text-3xl md:text-4xl text-tc-primary mb-4">{active.title}</h2>
              <p className="text-tc-accent italic font-serif text-lg mb-6">{active.excerpt}</p>
              <p className="text-tc-muted leading-relaxed whitespace-pre-line">{active.content}</p>
            </article>
          )}

          {/* Chat */}
          <section className="bg-tc-bg border border-tc rounded-3xl overflow-hidden" data-testid="chat-section">
            <div className="bg-tc-primary text-tc-bg p-6 flex items-center gap-3">
              <Sparkles className="w-5 h-5" />
              <div>
                <div className="font-serif text-2xl">Radice</div>
                <div className="text-xs opacity-80">Una guida AI per le tue domande sulla vita alternativa</div>
              </div>
            </div>
            <div className="p-6 max-h-[400px] overflow-y-auto space-y-4" data-testid="chat-messages">
              {messages.map(m => (
                <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-5 py-3 text-sm leading-relaxed ${m.role === 'user' ? 'bg-tc-accent text-tc-bg' : 'bg-tc-surface text-tc-text border border-tc/50'}`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start"><div className="bg-tc-surface rounded-2xl px-5 py-3 text-tc-muted italic text-sm">Radice ascolta…</div></div>
              )}
              <div ref={endRef} />
            </div>
            <form onSubmit={send} className="border-t border-tc p-4 flex gap-3" data-testid="chat-form">
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Scrivi una domanda, una sensazione, un dubbio…"
                className="flex-1 bg-transparent outline-none text-sm py-2"
                data-testid="chat-input"
              />
              <button type="submit" disabled={busy || !input.trim()}
                className="bg-tc-primary text-tc-bg rounded-full p-3 disabled:opacity-40 hover:bg-tc-primary/90 transition-colors"
                data-testid="chat-send">
                <Send className="w-4 h-4" />
              </button>
            </form>
          </section>
        </div>
      </div>

      {showForm && <NewArticleModal onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); loadArticles(); toast.success("Articolo inviato. Sarà visibile dopo l'approvazione."); }} />}
    </main>
  );
}

function NewArticleModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", excerpt: "", content: "", category: "pratica", read_time: "5 min" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/articles`, form);
      onSaved();
    } catch { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-tc-primary/40 backdrop-blur-sm z-40 flex items-center justify-center p-4" data-testid="new-article-modal">
      <div className="bg-tc-bg rounded-3xl max-w-2xl w-full p-8 border border-tc relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-tc-muted hover:text-tc-primary" data-testid="close-article-modal"><X /></button>
        <h2 className="font-serif text-3xl text-tc-primary mb-2">Scrivi un articolo</h2>
        <p className="text-sm text-tc-muted mb-6">Sarà visibile dopo la moderazione.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-tc-muted">Categoria</label>
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
              className="w-full bg-transparent border-b border-tc py-3 outline-none focus:border-tc-primary" data-testid="art-cat">
              <option value="filosofia">Filosofia</option>
              <option value="pratica">Pratica</option>
              <option value="comunita">Comunità</option>
              <option value="spiritualita">Spiritualità</option>
              <option value="ecologia">Ecologia</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-tc-muted">Titolo</label>
            <input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full bg-transparent border-b border-tc py-3 outline-none focus:border-tc-primary" data-testid="art-title" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-tc-muted">Sottotitolo / Estratto</label>
            <input required value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })}
              className="w-full bg-transparent border-b border-tc py-3 outline-none focus:border-tc-primary" data-testid="art-excerpt" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-tc-muted">Contenuto</label>
            <textarea required rows={10} value={form.content} onChange={e => setForm({ ...form, content: e.target.value })}
              className="w-full bg-transparent border-b border-tc py-3 outline-none focus:border-tc-primary resize-none" data-testid="art-content" />
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="art-submit">
            {busy ? "Invio…" : "Invia per approvazione"}
          </button>
        </form>
      </div>
    </div>
  );
}
